import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/api/helpers";
import { fetchWithAuth } from "@/utils/fetchWithAuth";

export interface SlotCreatedRow {
  member: string;
  location: string;
  room: string;
  slotStartDate: string;
  time: string;
  hours: number;
  ref: string;
}
export interface SlotChangedRow {
  member: string;
  location: string;
  room: string;
  change: string;
  when: string;
  ref: string;
}
export interface SlotCancelledRow {
  member: string;
  location: string;
  room: string;
  ref: string;
  hours: number;
  frequency: string;
  summary: string; // e.g. "3 hours weekly"
  endDate: string; // YYYY-MM-DD — the date the slot stops
  occurrences: number;
}
export interface SlotActivity {
  weekStart: string; // YYYY-MM-DD (the Tuesday)
  weekEnd: string; // YYYY-MM-DD (following Tuesday, exclusive)
  created: SlotCreatedRow[];
  changed: SlotChangedRow[];
  cancelled: SlotCancelledRow[];
}

/**
 * Admin-only slot activity for a Tuesday→Tuesday week (Created / Changed / Cancelled). Pass a
 * `weekStart` (YYYY-MM-DD) to view a specific week; omit for the current week. The server snaps any
 * date to its containing Tuesday.
 */
export function useAdminSlotActivity(enabled: boolean, weekStart?: string) {
  return useQuery<SlotActivity>({
    queryKey: ["admin-slot-activity", weekStart ?? "current"],
    queryFn: async () => {
      const url = weekStart
        ? `${API_BASE_URL}/api/admin/slot-activity?weekStart=${weekStart}`
        : `${API_BASE_URL}/api/admin/slot-activity`;
      const res = await fetchWithAuth(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load slot activity");
      return res.json();
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
